import React, { useState, useEffect, createContext, useContext } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Settings, GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';

// Context for KPI settings
const KPISettingsContext = createContext(null);

// Hook to use KPI settings
export const useKPISettings = (pageId) => {
  const context = useContext(KPISettingsContext);
  if (!context) {
    // Return default behavior if no provider
    return {
      isVisible: () => true,
      getOrder: (cards) => cards,
      settings: {}
    };
  }
  return {
    isVisible: (cardId) => context.isCardVisible(pageId, cardId),
    getOrder: (cards) => context.getCardOrder(pageId, cards),
    settings: context.getPageSettings(pageId)
  };
};

// Provider component
export const KPISettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('kpi_settings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading KPI settings:', e);
      }
    }
  }, []);

  // Save settings to localStorage
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('kpi_settings', JSON.stringify(newSettings));
  };

  const getPageSettings = (pageId) => {
    return settings[pageId] || { visible: {}, order: [] };
  };

  const updatePageSettings = (pageId, pageSettings) => {
    const newSettings = { ...settings, [pageId]: pageSettings };
    saveSettings(newSettings);
  };

  const isCardVisible = (pageId, cardId) => {
    const pageSettings = settings[pageId];
    if (!pageSettings || !pageSettings.visible || pageSettings.visible[cardId] === undefined) {
      return true; // Default to visible
    }
    return pageSettings.visible[cardId];
  };

  const getCardOrder = (pageId, cards) => {
    const pageSettings = settings[pageId];
    if (!pageSettings || !pageSettings.order || pageSettings.order.length === 0) {
      return cards; // Return original order
    }
    
    // Sort cards based on saved order
    const orderMap = {};
    pageSettings.order.forEach((id, index) => {
      orderMap[id] = index;
    });
    
    return [...cards].sort((a, b) => {
      const orderA = orderMap[a.id] !== undefined ? orderMap[a.id] : 999;
      const orderB = orderMap[b.id] !== undefined ? orderMap[b.id] : 999;
      return orderA - orderB;
    });
  };

  return (
    <KPISettingsContext.Provider value={{ 
      settings, 
      getPageSettings, 
      updatePageSettings, 
      isCardVisible, 
      getCardOrder 
    }}>
      {children}
    </KPISettingsContext.Provider>
  );
};

// Settings button component
export const KPISettingsButton = ({ pageId, cards, onSettingsChange }) => {
  const [open, setOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState({ visible: {}, order: [] });
  const [draggedItem, setDraggedItem] = useState(null);
  const context = useContext(KPISettingsContext);

  // Load settings when dialog opens
  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem('kpi_settings');
      if (saved) {
        try {
          const allSettings = JSON.parse(saved);
          const pageSettings = allSettings[pageId] || { visible: {}, order: [] };
          
          // Initialize visibility for all cards
          const visible = { ...pageSettings.visible };
          cards.forEach(card => {
            if (visible[card.id] === undefined) {
              visible[card.id] = true;
            }
          });
          
          // Initialize order
          let order = pageSettings.order || [];
          if (order.length === 0) {
            order = cards.map(c => c.id);
          }
          
          setLocalSettings({ visible, order });
        } catch (e) {
          initializeDefaults();
        }
      } else {
        initializeDefaults();
      }
    }
  }, [open, pageId, cards]);

  const initializeDefaults = () => {
    const visible = {};
    cards.forEach(card => {
      visible[card.id] = true;
    });
    setLocalSettings({
      visible,
      order: cards.map(c => c.id)
    });
  };

  const toggleVisibility = (cardId) => {
    setLocalSettings(prev => ({
      ...prev,
      visible: { ...prev.visible, [cardId]: !prev.visible[cardId] }
    }));
  };

  const handleDragStart = (e, cardId) => {
    setDraggedItem(cardId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, cardId) => {
    e.preventDefault();
    if (draggedItem === cardId) return;

    setLocalSettings(prev => {
      const newOrder = [...prev.order];
      const draggedIndex = newOrder.indexOf(draggedItem);
      const targetIndex = newOrder.indexOf(cardId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedItem);
      }
      
      return { ...prev, order: newOrder };
    });
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const saveAndClose = () => {
    // Save to localStorage
    const saved = localStorage.getItem('kpi_settings');
    const allSettings = saved ? JSON.parse(saved) : {};
    allSettings[pageId] = localSettings;
    localStorage.setItem('kpi_settings', JSON.stringify(allSettings));
    
    // Update context if available (this triggers re-render)
    if (context && context.updatePageSettings) {
      context.updatePageSettings(pageId, localSettings);
    }
    
    // Notify parent
    if (onSettingsChange) {
      onSettingsChange(localSettings);
    }
    
    setOpen(false);
  };

  const resetDefaults = () => {
    initializeDefaults();
  };

  // Get ordered cards for display
  const orderedCards = localSettings.order.length > 0
    ? localSettings.order.map(id => cards.find(c => c.id === id)).filter(Boolean)
    : cards;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Paramètres KPI">
          <Settings className="h-4 w-4 text-slate-500 hover:text-slate-700" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Paramètres des cartes KPI
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-slate-500 mb-4">
            Glissez pour réordonner. Cliquez sur l&apos;œil pour afficher/masquer.
          </p>
          
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {orderedCards.map((card) => (
              <div
                key={card.id}
                draggable
                onDragStart={(e) => handleDragStart(e, card.id)}
                onDragOver={(e) => handleDragOver(e, card.id)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 bg-white border rounded-lg cursor-move transition-all ${
                  draggedItem === card.id ? 'opacity-50 border-blue-400' : 'border-slate-200 hover:border-slate-300'
                } ${!localSettings.visible[card.id] ? 'opacity-60' : ''}`}
              >
                <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
                
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${!localSettings.visible[card.id] ? 'text-slate-400' : 'text-slate-700'}`}>
                    {card.label}
                  </p>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={() => toggleVisibility(card.id)}
                >
                  {localSettings.visible[card.id] ? (
                    <Eye className="h-4 w-4 text-green-600" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-slate-400" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" size="sm" onClick={resetDefaults}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Réinitialiser
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button size="sm" onClick={saveAndClose}>
              Sauvegarder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KPISettingsButton;
